<template>
  <div>
    <LanguageSelector />
    <div class="content-header row"></div>
    <div class="content-body">
      <section id="basic-datatable">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-content">
                <div class="card-header">
                  <h5 class="card-title">Manage Proposals</h5>
                  <div class="heading-elements">
                    <ul class="list-inline mb-0">
                      <li>
                        <router-link :to="{name: 'CreateContractProposalPage' }">
                          <button type="button" class="btn btn-primary">
                            <span class="align-middle ml-25">Create Proposal</span>
                          </button>
                        </router-link>
                      </li>
                      <li>
                        <div class="has-icon-left">
                          <select class="form-control" v-model="creationStatusFilter" @change="filterByData()">
                            <option selected>All Creation Status...</option>
                            <option value="approved">Approved</option>
                            <option value="needs_approval_by_admin">Needs Approval by Admin</option>
                            <option value="needs_approval_by_landlord">Needs Approval by Landlord</option>
                            <option value="draft_by_admin">Draft by Admin</option>
                            <option value="draft_by_landlord">Draft by Landlord</option>
                            <option value="archived">Archived</option>
                          </select>
                          <div class="form-control-position">
                            <em class="bx bx-filter font-medium-1"></em>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div class="has-icon-left">
                          <select class="form-control" v-model="negotiationStatusFilter" @change="filterByData()">
                            <option selected>All Negotiation Status...</option>
                            <option value="accepted_by_landlord">Accepted by Landlord</option>
                            <option value="accepted_by_all">Accepted by All</option>
                            <option value="change_requested_by_tenant">Change Requested by Tenant</option>
                            <option value="declined_by_tenant">Declined by Tenant</option>
                            <option value="archived">Archived</option>
                          </select>
                          <div class="form-control-position">
                            <em class="bx bx-filter font-medium-1"></em>
                          </div>
                        </div>
                      </li>
                      <li v-if="isSuperadmin">
                        <div class="has-icon-left">
                          <select class="form-control" v-model="countryFilter" @change="filterByData()">
                            <option selected>All Countries...</option>
                            <option v-for="(country, index) in usersCountries" :value="country" :key="index">{{
                            countryName(country) }}</option>
                          </select>
                          <div class="form-control-position">
                            <em class="bx bx-map font-medium-1"></em>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="card-body card-dashboard">
                  <div class="text-center">
                    <div class="spinner-border text-primary" role="status" v-if="loadingData" style="opacity: .6">
                      <span class="sr-only">Loading...</span>
                    </div>
                  </div>
                  <div class="text-center mt-1" v-if="!proposals.length && !loadingData">
                    <p>No Proposal Found...</p>
                  </div>
                  <div class="table-responsive" v-if="proposals.length">
                    <table class="table zero-configuration" :aria-label="$t('proposals_aria_label_proposal_list')">
                      <thead>
                        <tr>
                          <th>Address</th>
                          <th>Landlord</th>
                          <th>Tenant</th>
                          <th>Creation Status</th>
                          <th>Negotiation Status</th>
                          <th>Country</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(proposal, index) in proposals" :key="index">
                          <td class="templateName capitalize">
                            <span v-if="proposal.property.address">
                              {{ proposal.property.address.street }}, {{ countryName(proposal.country) }}
                            </span>
                            <span v-else>-</span>
                            <br />
                            <router-link :to="{name: 'ContractProposalSinglePage', params: {uuid: proposal.id} }"
                              href="#" style="margin-top: 5px;">
                              View Details
                            </router-link>
                          </td>
                          <td>
                            <span v-if="proposal.landlord.user">{{ (proposal.landlord.user.name) ?
                            proposal.landlord.user.name : '-' }}</span>
                            <span v-else>{{ (proposal.landlord.invite.name) ? proposal.landlord.invite.name : '-'
                            }}</span>
                          </td>
                          <td v-if="proposal.tenants">
                            <span v-if="proposal.tenants[0].user">{{ (proposal.tenants[0].user.name) ?
                            proposal.tenants[0].user.name : '-' }}</span>
                            <span v-else>{{ (proposal.tenants[0].invite.name) ? proposal.tenants[0].invite.name : '-'
                            }}</span>
                          </td>
                          <td v-else>-</td>
                          <td class="capitalize">
                            <span v-if="proposal.creationStatus == 'draft_by_admin'"><em
                                class="bx bxs-circle default font-small-1 mr-50"></em>{{
                                proposal.creationStatus.replaceAll("_", " ") }}</span>
                            <span v-if="proposal.creationStatus == 'draft_by_landlord'"><em
                                class="bx bxs-circle default font-small-1 mr-50"></em>{{
                                proposal.creationStatus.replaceAll("_", " ") }}</span>
                            <span v-if="proposal.creationStatus == 'needs_approval_by_admin'"><em
                                class="bx bxs-circle info font-small-1 mr-50"></em>{{
                                proposal.creationStatus.replaceAll("_", " ") }}</span>
                            <span v-if="proposal.creationStatus == 'needs_approval_by_landlord'"><em
                                class="bx bxs-circle info font-small-1 mr-50"></em>{{
                                proposal.creationStatus.replaceAll("_", " ") }}</span>
                            <span v-if="proposal.creationStatus == 'approved'"><em
                                class="bx bxs-circle success font-small-1 mr-50"></em>{{ proposal.creationStatus
                                }}</span>
                            <span v-if="proposal.creationStatus == 'archived'"><em
                                class="bx bxs-circle warning font-small-1 mr-50"></em>{{ proposal.creationStatus
                                }}</span>
                          </td>
                          <td class="capitalize">
                            <span v-if="!proposal.negotiationStatus">-</span>
                            <span
                              v-if="proposal.negotiationStatus && proposal.negotiationStatus == 'accepted_by_landlord'"><em
                                class="bx bxs-circle success font-small-1 mr-50"></em>{{
                                proposal.negotiationStatus.replaceAll("_", " ") }}</span>
                            <span
                              v-if="proposal.negotiationStatus && proposal.negotiationStatus == 'accepted_by_all'"><em
                                class="bx bxs-circle success font-small-1 mr-50"></em>{{
                                proposal.negotiationStatus.replaceAll("_", " ") }}</span>
                            <span
                              v-if="proposal.negotiationStatus && proposal.negotiationStatus == 'declined_by_tenant'"><em
                                class="bx bxs-circle danger font-small-1 mr-50"></em>{{
                                proposal.negotiationStatus.replaceAll("_", " ") }}</span>
                            <span
                              v-if="proposal.negotiationStatus && proposal.negotiationStatus == 'change_requested_by_tenant'"><em
                                class="bx bxs-circle info font-small-1 mr-50"></em>{{
                                proposal.negotiationStatus.replaceAll("_", " ") }}</span>
                            <span v-if="proposal.negotiationStatus && proposal.negotiationStatus == 'archived'"><em
                                class="bx bxs-circle warning font-small-1 mr-50"></em>{{ proposal.negotiationStatus
                                }}</span>
                          </td>
                          <td>{{ countryName(proposal.country) }}</td>
                          <td>{{ proposal.createdAt.split('T')[0] }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getCountryName } from "@/app-assets/js/get-countries.js";
import "@/app-assets/js/scripts/extensions/ext-component-block-ui.js";
import Alert from "@/components/Alerts";

export default {
  data() {
    return {
      proposals: [],
      loadingData: true,
      usersCountries: [],
      countryFilter: 'All Countries...',
      creationStatusFilter: 'All Creation Status...',
      negotiationStatusFilter: 'All Negotiation Status...'
    };
  },

  components: {
    Alert
  },

  computed: {
    isSuperadmin() {
      return this.$store.state.currentUser.user.type === "superadmin";
    },
  },

  methods: {
    getContractProposals() {
      this.loadingData = true;
      this.proposals = [];
      this.$apiCall
        .get("/v1/contracts/proposals")
        .then((response) => {
          response.data.data.forEach((element) => {
            if (
              !this.usersCountries.includes(element.country) &&
              element.country
            ) {
              this.usersCountries.push(element.country);
            }
          });
          console.log(response);
          this.loadingData = false;
          this.proposals = response.data.data;
          this.initDataTable();
        })
        .catch((err) => {
          console.log(err);
        });
    },

    filterApiCall(data) {
      console.log("filter", data)
      let self = this;
      self.$apiCall
        .get(`/v1/contracts/proposals?${data}`)
        .then(response => {
          self.loadingData = false;
          self.proposals = response.data.data;
          self.initDataTable();
        })
    },
    filterByData() {
      let self = this;
      self.proposals = [];
      let filterValue = ""
      self.loadingData = true;
      let data = [];
      if (self.countryFilter != "All Countries...") {
        data.push("country=" + self.countryFilter)
      }
      if (self.negotiationStatusFilter != "All Negotiation Status...") {
        data.push("negotiationStatus=" + self.negotiationStatusFilter)
      }
      if (self.creationStatusFilter != "All Creation Status...") {
        data.push("creationStatus=" + self.creationStatusFilter)
      }

      let query = '';
      if (data.length) {
        query = data.join('&');
        self.filterApiCall(query)
      }

      else {
        self.proposals = []
        self.usersCountries = [];
        self.getContractProposals();
      }
    },

    initDataTable() {
      let self = this;
      setTimeout(function () {
        $(".zero-configuration").DataTable({
          bLengthChange: false,
          bFilter: false,
          order: [],
        });
      }, 100);
    },

    countryName(element) {
      if (element) {
        return getCountryName(element.toUpperCase());
      }
    },

    getActiveCountries() {
      const countries = localStorage.getItem('activeCountries');
      this.countries = (countries && countries != '') ? countries.split(',') : ["LB", "JO"];
    },
  },

  mounted() {
    this.getContractProposals();
    this.getActiveCountries();
    if (navigator.appVersion.indexOf("Chrome/") != -1) {
      $("li > a > .btn.btn-primary").addClass("mt-chrome")
    } else {
      $("li > a > .btn.btn-primary").addClass("mt-firefox")
    }
  },
};
</script>

<style scoped>
.zero-configuration td {
  white-space: nowrap;
}
.heading-elements .list-inline li {
  margin-right: 5px;
}
</style>